import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiHome, 
  FiFolder, 
  FiCalendar, 
  FiUsers, 
  FiFileText, 
  FiSettings, 
  FiLogOut, 
  FiChevronLeft, 
  FiChevronRight, 
  FiDollarSign, 
  FiBarChart2, 
  FiMessageSquare, 
  FiClock,
  FiMail,
  FiLayers,
  FiClipboard,
  FiTrendingDown,
  FiTrendingUp,
  FiHelpCircle
} from 'react-icons/fi';

const SidebarContainer = styled.div`
  position: fixed;
  left: 0;
  height: 100vh;
  width: ${props => props.$isOpen ? "240px" : "70px"};
  background-color: var(--army-green);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  z-index: 10;
  display: flex;
  flex-direction: column;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: -12px;
  top: 20px;
  height: 24px;
  width: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  z-index: 20;
  padding: 0;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1rem 0;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
  margin-bottom: 1.5rem;
`;

const Logo = styled.img`
  max-width: ${props => props.$isOpen ? "140px" : "40px"};
  transition: max-width 0.3s ease;
`;

const RoleBadge = styled.div`
  display: ${props => props.$isOpen ? "block" : "none"};
  text-align: center;
  font-size: 0.75rem;
  font-weight: bold;
  color: var(--old-silver-light);
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const MenuContainer = styled.div`
  padding: 0 0.5rem;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 250px); /* Adjust based on logo, role badge, and logout section */
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--old-silver-light);
    border-radius: 3px;
    
    &:hover {
      background: var(--white);
    }
  }
`;

const MenuItem = styled.div`
  padding: 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: var(--army-green-dark);
  }
`;

const MenuIcon = styled.div`
  font-size: 1.25rem;
  color: var(--white);
  margin-right: ${props => props.$isOpen ? "0.75rem" : "0"};
  
  ${MenuItem}:hover & {
    color: var(--old-silver-light);
  }
`;

const MenuText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: opacity 0.3s;
  white-space: nowrap;
  color: var(--white);
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background-color: var(--army-green-light);
  margin: 1.5rem 0;
`;

const LogoutContainer = styled.div`
  margin-top: auto;
  padding: 0 0.5rem;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  
  &:hover {
    text-decoration: none;
  }
`;

function ChakraSidebar({ userRole = 'lawyer', onLogout }) {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  
  // Get user permissions from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userPermissions = userData.permissions || [];
  
  // Initialize sidebar state from localStorage if available
  useEffect(() => {
    const storedIsOpen = localStorage.getItem('sidebarIsOpen') === 'false' ? false : true;
    setIsOpen(storedIsOpen);
  }, []);
  
  // Update localStorage when state changes
  const toggleSidebar = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    localStorage.setItem('sidebarIsOpen', newState.toString());
  };
  
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userType');
      localStorage.removeItem('token');
      navigate('/login');
    }
  };
  
  // Instead of using an external placeholder service, use a base64 encoded placeholder
  const fallbackLogo = "data:image/png;base64,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";

 // Menu items for different roles
  const directorMenuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/painel' },
    { name: 'Dashboard Planejador', icon: FiBarChart2, path: '/painel/dashboard-planejador' },
    { name: 'Dashboard Finanças', icon: FiDollarSign, path: '/painel/dashboard-financas' },
    { name: 'Contas a Receber', icon: FiTrendingUp, path: '/painel/contas-receber' },
    { name: 'Contas a Pagar', icon: FiTrendingDown, path: '/painel/contas-pagar' },
    { name: 'Email', icon: FiMail, path: '/painel/email' },
    { name: 'Registros', icon: FiFolder, path: '/painel/registros' },
    { name: 'Equipe', icon: FiUsers, path: '/painel/equipe' },
    { name: 'Clientes', icon: FiUsers, path: '/painel/clientes' },
    { name: 'Tipos de Cliente', icon: FiLayers, path: '/painel/tipos-cliente' },
    { name: 'Planejador', icon: FiClipboard, path: '/painel/planner' },
    { name: 'FAQ', icon: FiHelpCircle, path: '/painel/faq' },
    { name: 'Relatórios', icon: FiBarChart2, path: '/painel/relatorios' },
    { name: 'Termos de Uso', icon: FiFileText, path: '/painel/termos-de-uso' },
    { name: 'Política de Privacidade', icon: FiFileText, path: '/painel/politica-privacidade' },
    { name: 'Configurações', icon: FiSettings, path: '/painel/configuracoes' },
  ];

  const lawyerMenuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/painel' },
    { name: 'Email', icon: FiMail, path: '/painel/email' },
    { name: 'Meus Registros', icon: FiFolder, path: '/painel/meu-registros' },
    { name: 'Agenda', icon: FiCalendar, path: '/painel/agenda' },
    { name: 'Prazos', icon: FiClock, path: '/painel/prazos' },
    { name: 'Documentos', icon: FiFileText, path: '/painel/documentos' },
    { name: 'Clientes', icon: FiUsers, path: '/painel/clientes' },
    { name: 'Planejador', icon: FiClipboard, path: '/painel/planner' },
    { name: 'FAQ', icon: FiHelpCircle, path: '/painel/faq' },
    { name: 'Mensagens', icon: FiMessageSquare, path: '/painel/mensagens' },
  ];

  // Create combined menu items for master role (all unique items from both roles)
  const masterMenuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/painel' },
    { name: 'Dashboard Planejador', icon: FiBarChart2, path: '/painel/dashboard-planejador' },
    { name: 'Dashboard Finanças', icon: FiDollarSign, path: '/painel/dashboard-financas' },
    { name: 'Contas a Receber', icon: FiTrendingUp, path: '/painel/contas-receber' },
    { name: 'Contas a Pagar', icon: FiTrendingDown, path: '/painel/contas-pagar' },
    { name: 'Email', icon: FiMail, path: '/painel/email' },
    { name: 'Registros', icon: FiFolder, path: '/painel/registros' },
    { name: 'Meus Registros', icon: FiFolder, path: '/painel/meu-registros' },
    { name: 'Equipe', icon: FiUsers, path: '/painel/equipe' },
    { name: 'Agenda', icon: FiCalendar, path: '/painel/agenda' },
    { name: 'Prazos', icon: FiClock, path: '/painel/prazos' },
    { name: 'Documentos', icon: FiFileText, path: '/painel/documentos' },
    { name: 'Clientes', icon: FiUsers, path: '/painel/clientes' },
    { name: 'Tipos de Cliente', icon: FiLayers, path: '/painel/tipos-cliente' },
    { name: 'Planejador', icon: FiClipboard, path: '/painel/planner' },
    { name: 'FAQ', icon: FiHelpCircle, path: '/painel/faq' },
    { name: 'Mensagens', icon: FiMessageSquare, path: '/painel/mensagens' },
    { name: 'Relatórios', icon: FiBarChart2, path: '/painel/relatorios' },
    { name: 'Termos de Uso', icon: FiFileText, path: '/painel/termos-de-uso' },
    { name: 'Política de Privacidade', icon: FiFileText, path: '/painel/politica-privacidade' },
    { name: 'Configurações', icon: FiSettings, path: '/painel/configuracoes' },
  ];
  
  // Finance menu items for users with Finance permission
  const financeMenuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/painel' },
    { name: 'Dashboard Finanças', icon: FiDollarSign, path: '/painel/dashboard-financas' },
    { name: 'Contas a Receber', icon: FiTrendingUp, path: '/painel/contas-receber' },
    { name: 'Contas a Pagar', icon: FiTrendingDown, path: '/painel/contas-pagar' },
    { name: 'Email', icon: FiMail, path: '/painel/email' },
    { name: 'Clientes', icon: FiUsers, path: '/painel/clientes' },
    { name: 'Planejador', icon: FiClipboard, path: '/painel/planner' },
    { name: 'FAQ', icon: FiHelpCircle, path: '/painel/faq' },
    { name: 'Mensagens', icon: FiMessageSquare, path: '/painel/mensagens' },
  ];

  // Gestor menu items
  const gestorMenuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/painel' },
    { name: 'Dashboard Planejador', icon: FiBarChart2, path: '/painel/dashboard-planejador' },
    { name: 'Email', icon: FiMail, path: '/painel/email' },
    { name: 'Agenda', icon: FiCalendar, path: '/painel/agenda' },
    { name: 'Equipe', icon: FiUsers, path: '/painel/equipe' },
    { name: 'Clientes', icon: FiUsers, path: '/painel/clientes' },
    { name: 'Tipos de Cliente', icon: FiLayers, path: '/painel/tipos-cliente' },
    { name: 'Planejador', icon: FiClipboard, path: '/painel/planner' },
    { name: 'FAQ', icon: FiHelpCircle, path: '/painel/faq' },
    { name: 'Configurações', icon: FiSettings, path: '/painel/configuracoes' },
  ];

  // Select menu items based on user role and permissions
  let menuItems;
  if (userRole === 'master') {
    menuItems = masterMenuItems;
  } else if (userRole === 'diretoria') {
    menuItems = directorMenuItems;
  } else if (userRole === 'gestor' || userPermissions.includes('Gestor')) {
    menuItems = gestorMenuItems;
  } else if (userPermissions.includes('Financeiro')) {
    // If user has Finance permission, show finance-specific menu
    menuItems = financeMenuItems;
  } else {
    menuItems = lawyerMenuItems;
  }

  return (
    <SidebarContainer $isOpen={isOpen} data-sidebarstate={isOpen ? "open" : "closed"}>
      {/* Toggle button */}
      <ToggleButton onClick={toggleSidebar}>
        {isOpen ? <FiChevronLeft /> : <FiChevronRight />}
      </ToggleButton>

      <SidebarContent>
        {/* Logo */}
        <LogoContainer>
          <Logo 
            src={process.env.PUBLIC_URL + '/logo-veridicus.png'} 
            alt="Veridicus" 
            $isOpen={isOpen} 
            onError={(e) => { e.target.src = fallbackLogo; }}
          />
        </LogoContainer>
        
        {/* Role badge */}
        <RoleBadge $isOpen={isOpen}>
          {userRole === 'master' || userRole === 'diretoria' 
            ? 'Diretoria' 
            : 'Associado'}
        </RoleBadge>

        {/* Menu Items */}
        <MenuContainer>
          {menuItems.map((item) => (
            <NavLink key={item.name} to={item.path}>
              <MenuItem>
                <MenuIcon $isOpen={isOpen}>
                  <item.icon />
                </MenuIcon>
                <MenuText $isOpen={isOpen}>
                  {item.name}
                </MenuText>
              </MenuItem>
            </NavLink>
          ))}
        </MenuContainer>

        <Divider />

        {/* Logout */}
        <LogoutContainer>
          <MenuItem onClick={handleLogout}>
            <MenuIcon $isOpen={isOpen}>
              <FiLogOut />
            </MenuIcon>
            <MenuText $isOpen={isOpen}>
              Sair
            </MenuText>
          </MenuItem>
        </LogoutContainer>
      </SidebarContent>
    </SidebarContainer>
  );
}

export default ChakraSidebar;